/**
 * KRA Photo Journal – DownloadModal
 *
 * Compact modal that pops up from the grid thumbnail download icon. Lets
 * the user adjust the SEO filename via the same chip builder used in the
 * lightbox, then triggers the WordPress-optimised download — without
 * having to open the full-screen lightbox first.
 */

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FilenameBuilder } from "./FilenameBuilder.js";
import {
  composeFilenameSlug,
  selectionFromTags,
  type FilenameSelection,
} from "../lib/seo-filename.js";
import type { GalleryFile, GalleryTags } from "../lib/api.js";
import { cn } from "../lib/utils.js";

interface Props {
  file: GalleryFile;
  thumbnailUrl: string;
  tags: GalleryTags | null;
  onClose: () => void;
  /** Build the worker download URL given the optional SEO slug. */
  buildDownloadUrl: (fileId: string, slug: string | null) => string;
  /** Save handler — same `saveFile` used by the lightbox. */
  onDownload: (downloadUrl: string, filename: string, directImageUrl: string) => Promise<void>;
}

export function DownloadModal({ file, thumbnailUrl, tags, onClose, buildDownloadUrl, onDownload }: Props) {
  const [selection, setSelection] = useState<FilenameSelection>(() => selectionFromTags(tags));
  const [saving, setSaving] = useState(false);

  // Reset selection when the file changes (modal can be reopened on a
  // different photo without unmounting)
  useEffect(() => {
    setSelection(selectionFromTags(tags));
  }, [tags, file.fileId]);

  const slug = useMemo(() => composeFilenameSlug(selection, file.fileId), [selection, file.fileId]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleDownload = async () => {
    if (saving) return;
    setSaving(true);
    const filename = slug ? `${slug}.webp` : `photo_${file.fileId.slice(-8)}_web.webp`;
    try {
      await onDownload(buildDownloadUrl(file.fileId, slug), filename, thumbnailUrl);
      onClose();
    } catch {
      // user cancelled or fetch error — keep modal open so they can retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div
        key="dl-modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm"
        onClick={onClose}
      />

      <motion.div
        key="dl-modal-content"
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.96 }}
        transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={cn(
          "fixed inset-0 z-[91] flex items-center justify-center p-4",
          "pointer-events-none",
        )}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "pointer-events-auto",
            "w-full max-w-2xl max-h-[90vh] overflow-y-auto",
            "rounded-2xl bg-[#1a1a1a]/97 border border-white/10",
            "shadow-2xl shadow-black/50",
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={thumbnailUrl}
                alt=""
                className="w-10 h-10 rounded-md object-cover flex-shrink-0 bg-white/5"
              />
              <div className="min-w-0">
                <h2 className="text-white text-sm font-semibold leading-tight">
                  Tải ảnh tối ưu cho web
                </h2>
                <p className="text-white/50 text-[11px] truncate">{file.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={cn(
                "w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0",
                "bg-white/5 hover:bg-white/15 text-white/70 hover:text-white",
                "transition-colors",
              )}
              aria-label="Đóng"
              title="Đóng (Esc)"
            >
              <svg width={11} height={11} viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M1 1l11 11M12 1L1 12"/>
              </svg>
            </button>
          </div>

          {/* Body — chip builder */}
          <div className="px-5 py-4">
            <FilenameBuilder
              selection={selection}
              onSelectionChange={setSelection}
              previewSlug={slug}
              previewExtension="webp"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10">
            <button
              onClick={onClose}
              disabled={saving}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium",
                "bg-transparent text-white/70 hover:text-white hover:bg-white/10",
                "transition-colors",
              )}
            >
              Hủy
            </button>
            <button
              onClick={handleDownload}
              disabled={saving}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2 rounded-full",
                "bg-[#B76E6E] hover:bg-[#a55e5e] transition-colors",
                "text-white text-sm font-medium shadow-lg shadow-black/30",
                "ring-1 ring-white/20",
                saving && "opacity-60 cursor-not-allowed",
              )}
            >
              {saving ? (
                <>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="animate-spin">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                  </svg>
                  <span>Đang tải…</span>
                </>
              ) : (
                <>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v13M7 11l5 5 5-5"/>
                    <path d="M5 20h14"/>
                  </svg>
                  <span>Tải tối ưu web</span>
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
}
